import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useQueryState } from '@/hooks/use-query-state'
import { EMPTY_VALUE, formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import type { IzinRow } from '@/services/cube/hr'
import { hrApi, type HrTask } from '@/services/http/hr'
import { CategoryBadge } from '@/features/hr/AttendancePage'
import { ConflictList, HourFixDialog } from '@/features/hr/conflicts'
import { HrPageHeader } from '@/features/hr/HrPageHeader'
import {
  DetailGrid,
  PersonCell,
  RecordActions,
  RecordHistory,
  type ActionSpec,
} from '@/features/hr/parts'
import { ExportButton } from '@/components/data-table/ExportButton'
import { tasksExport } from '@/features/hr/exports'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const m = t.tasks
const TABS = [
  'all',
  'kalici_hata',
  'firma_belirsiz',
  'firma_bulunamadi',
  'silinen_hareket',
  'izin_durumu',
  'izin_cakismasi',
] as const
type Tab = (typeof TABS)[number]

/** Kalıcı hata ve firma belirsizliği: düzeltip tekrar gönder ya da yoksay. */
function taskActions(task: HrTask): ActionSpec[] {
  if (task.kind === 'silinen_hareket') {
    return [
      { type: 'hareket_dia_kalsin', needsReason: true },
      { type: 'hareket_dia_sil', needsReason: true, danger: true, confirm: m.diaDeleteConfirm },
    ]
  }
  if (task.kind === 'izin_durumu') return [{ type: 'izin_passgage_guncel_al' }]
  if (task.target_kind === 'izin') {
    return [
      { type: 'izin_tekrar_gonder' },
      { type: 'izin_passgage_guncel_al' },
      { type: 'izin_yoksay', needsReason: true },
    ]
  }
  return [
    { type: 'hareket_tekrar_gonder' },
    { type: 'hareket_passgage_guncel_al' },
    { type: 'hareket_yoksay', needsReason: true },
    { type: 'hareket_elle_girildi', needsReason: true },
  ]
}

function taskKey(task: HrTask): string {
  return `${task.target_kind}:${task.target_id}`
}

function RecordCell({ task }: { task: HrTask }) {
  const detail =
    task.target_kind === 'hareket' ? (t.girisCikis[task.detail ?? ''] ?? task.detail) : task.detail
  return (
    <span className="flex flex-col leading-tight">
      {t.interventions.target[task.target_kind]}
      <span className="text-text-muted text-xs">{detail ?? EMPTY_VALUE}</span>
    </span>
  )
}

function ProblemCell({ task }: { task: HrTask }) {
  if (task.kind === 'silinen_hareket') {
    return (
      <Badge tone="warning" size="sm">
        {m.deletedInPassgage}
      </Badge>
    )
  }
  if (task.kind === 'izin_durumu') {
    return (
      <Badge tone="info" size="sm">
        {m.passgageStatus[task.passgage_status ?? ''] ?? task.passgage_status}
      </Badge>
    )
  }
  return (
    <span className="flex flex-col items-start gap-0.5">
      <CategoryBadge category={task.kategori} />
      <span className="text-text-muted text-xs tabular-nums">{m.attempts(task.attempts)}</span>
    </span>
  )
}

function TaskSheet({ task, onClose }: { task: HrTask | undefined; onClose: () => void }) {
  return (
    <Sheet open={task !== undefined} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{task ? m.tabs[task.kind] : m.title}</SheetTitle>
          <SheetDescription>{task && m.hint[task.kind]}</SheetDescription>
        </SheetHeader>
        {task && (
          <SheetBody className="flex flex-col gap-4">
            <DetailGrid
              items={[
                [
                  m.columns.kisi,
                  <PersonCell key="p" name={task.personel_adi} sub={task.sicilno} />,
                ],
                [m.columns.firma, [task.firma, task.sube].filter(Boolean).join(' · ') || null],
                [m.columns.kayit, <RecordCell key="k" task={task} />],
                [m.columns.zaman, task.at ? formatDateTime(task.at) : null],
                ...(task.end_at
                  ? ([[t.detail.fields.bitis, formatDateTime(task.end_at)]] as [string, string][])
                  : []),
                [m.columns.sorun, <ProblemCell key="s" task={task} />],
                [t.detail.error, task.message],
                [m.columns.since, task.since ? formatDateTime(task.since) : null],
              ]}
            />
            <div className="flex flex-wrap items-center gap-3">
              <RecordActions
                kind={task.target_kind}
                id={task.target_id}
                actions={taskActions(task)}
              />
              {task.target_kind === 'izin' && (
                <Link
                  to={`/ik/izinler?id=${encodeURIComponent(task.target_id)}`}
                  className="text-brand text-sm underline-offset-2 hover:underline"
                >
                  {t.detail.openLeave}
                </Link>
              )}
            </div>
            <section className="border-border flex flex-col gap-2 border-t pt-4">
              <h3 className="text-sm font-medium">{t.detail.history}</h3>
              <div className="[--timeline-surface:var(--surface)]">
                <RecordHistory kind={task.target_kind} id={task.target_id} />
              </div>
            </section>
          </SheetBody>
        )}
      </SheetContent>
    </Sheet>
  )
}

export function TasksPage() {
  const query = useQueryState()
  const tab = (TABS as readonly string[]).includes(query.get('sekme') ?? '')
    ? (query.get('sekme') as Tab)
    : 'all'
  const selectedKey = query.get('is')
  const [hourFix, setHourFix] = useState<IzinRow | null>(null)
  const list = useQuery({ queryKey: queryKeys.hr.tasks(), queryFn: hrApi.tasks })
  const items = useMemo(() => list.data ?? [], [list.data])
  const counts = useMemo(() => {
    const result = Object.fromEntries(TABS.map((key) => [key, 0])) as Record<Tab, number>
    result.all = items.length
    for (const item of items) result[item.kind] += 1
    return result
  }, [items])
  const rows = tab === 'all' ? items : items.filter((item) => item.kind === tab)
  // Firması bulunamayan sicilin işleri yalnız İK onaycılarına gelir; sekme de yalnız onlara.
  const canApprove = useAuthStore((state) => state.hasPermission('hr.request:approve'))
  const visibleTabs = TABS.filter((key) => key !== 'firma_bulunamadi' || canApprove)
  const selected = items.find((item) => taskKey(item) === selectedKey)

  const cells: [string, (task: HrTask) => ReactNode][] = [
    [
      m.columns.zaman,
      (task) => (
        <span className="tabular-nums">{task.at ? formatDateTime(task.at) : EMPTY_VALUE}</span>
      ),
    ],
    [m.columns.kisi, (task) => <PersonCell name={task.personel_adi} sub={task.sicilno} />],
    [m.columns.firma, (task) => task.firma ?? EMPTY_VALUE],
    [m.columns.kayit, (task) => <RecordCell task={task} />],
    [m.columns.sorun, (task) => <ProblemCell task={task} />],
    [
      m.columns.since,
      (task) => (
        <span className="text-text-muted text-xs tabular-nums">
          {task.since ? formatDateTime(task.since) : EMPTY_VALUE}
        </span>
      ),
    ],
  ]

  return (
    <div className="flex flex-col gap-4">
      <HrPageHeader
        title={m.title}
        description={m.description}
        actions={<ExportButton filename="ik-islerim" {...tasksExport(rows)} />}
      />
      <Tabs
        value={tab}
        onValueChange={(value: Tab) => query.set({ sekme: value === 'all' ? undefined : value })}
      >
        <TabsList className="flex-wrap">
          {visibleTabs.map((key) => (
            <TabsTrigger key={key} value={key}>
              {m.tabs[key]}
              <span className="text-text-muted tabular-nums">{counts[key]}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {tab !== 'all' && <p className="text-text-secondary text-sm">{m.hint[tab]}</p>}
      {tab === 'izin_cakismasi' ? (
        <ConflictList onHourFix={setHourFix} />
      ) : list.isLoading ? (
        <Loading rows={5} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <Empty hint={items.length === 0 ? m.empty : m.emptyTab} />
      ) : (
        <>
          <p className="text-text-muted text-xs">{m.count(rows.length, items.length)}</p>
          <div className="border-border rounded-panel bg-surface overflow-x-auto border">
            <Table>
              <TableHeader>
                <TableRow>
                  {cells.map(([label]) => (
                    <TableHead key={label}>{label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((task) => (
                  <TableRow
                    key={taskKey(task)}
                    tabIndex={0}
                    className="cursor-pointer"
                    onClick={() => query.set({ is: taskKey(task) })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') query.set({ is: taskKey(task) })
                    }}
                  >
                    {cells.map(([label, cell]) => (
                      <TableCell key={label}>{cell(task)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      <TaskSheet task={selected} onClose={() => query.set({ is: undefined })} />
      <HourFixDialog key={hourFix?.id ?? 'none'} leave={hourFix} onClose={() => setHourFix(null)} />
    </div>
  )
}
